import { action, computed, observable, toJS } from "mobx";
import debug from "debug";
import { Store } from "@store";
import { stringNormalize } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { SetCallListMembrs, UserFragment } from "@graphql";

const log = debug("app:store:UserSelectorStore");

type OnSelectCallback = (user: UserFragment) => void;
type OnCancelCallback = () => void;

type OpenUserSelectorOptions = {
  includeActiveUser?: boolean;
  includeEmpty?: boolean;
  listId?: string;
  multi?: boolean;
  onCancel?: OnCancelCallback;
  onSelect: OnSelectCallback;
  excludeNonAdUsers?: boolean;
  removeDisabled?: boolean;
  selectedUsers?: string[];
  onlyAzureGroups?: string[];
};

export class UserSelectorStore {
  @observable private _isUserSelectorOpen = false;
  @observable private options: OpenUserSelectorOptions | null = null;
  @observable private _selectedUsers: string[] = [];

  @observable showDisabled = false;

  constructor(public readonly rootStore: Store) {}

  /**
   * USER SELECTOR DIALOG
   */
  @computed get isUserSelectorOpen() {
    return this._isUserSelectorOpen;
  }

  getUserList(_users: UserFragment[], teams: string[]) {
    const { user } = this.rootStore;

    let allUsers = _users;

    if (this.options?.onlyAzureGroups && this.options.onlyAzureGroups.length > 0) {
      allUsers = allUsers.filter((x) => {
        return (
          x.AzureGroups.length > 0 &&
          x.AzureGroups.some((team) => this.options.onlyAzureGroups.includes(team))
        );
      });
    }

    allUsers = this.options?.removeDisabled
      ? allUsers.filter((x) => x.Enabled)
      : allUsers;

    if (this.options?.excludeNonAdUsers) {
      allUsers = allUsers.filter((x) => !!x.AzureId);
    }

    const users = allUsers
      .filter(
        (item) =>
          (this.options && this.options.includeActiveUser) ||
          item._id !== user.myProfile._id
      )
      .map((u) => ({
        ...u,
        search: stringNormalize(`${u.Name}${u.Email}${u.Teams.join("")}`).toLowerCase(),
        style: { opacity: u.Enabled ? 1 : 0.5, paddingLeft: 8 }
      }));

    if (this.options && toJS(this.options).includeEmpty) {
      users.unshift({
        _id: null,
        Name: locale.Nobody,
        Enabled: true,
        Email: "",
        Teams: [],
        Deleted: false,
        search: locale.Nobody,
        style: null
      });
    }

    return teams.map((team) => ({
      team,
      users: users.filter((u) => u.Teams.includes(team))
    }));
  }

  @computed get selectedUsers() {
    return this._selectedUsers;
  }

  @computed get multi() {
    return Boolean(this.options && this.options.multi);
  }

  @action.bound updateSelectedUsers = (user: string) => {
    if (this._selectedUsers.includes(user)) {
      const idxEl = this._selectedUsers.indexOf(user);
      this._selectedUsers.splice(idxEl, 1);
    } else {
      this._selectedUsers.push(user);
    }
  };

  @action.bound open(options: OpenUserSelectorOptions) {
    this.options = options;
    this._isUserSelectorOpen = true;
    this._selectedUsers = options.selectedUsers;
  }

  @action.bound close() {
    this._isUserSelectorOpen = false;

    this.options.onCancel && this.options.onCancel();
    this.options = null;
  }

  @action.bound submit() {
    // todo: activate spinner
    SetCallListMembrs.func({
      callListId: this.options.listId,
      userIds: this.selectedUsers
    })
      .then(() => {
        this.close();
      })
      .catch((e) => {
        this.rootStore.snackbars.error(`Cannot add item to call list\nReason: ${e}`);
      });
  }

  @action.bound select(user: UserFragment) {
    log("select", this.options.onSelect, user);
    this.options.onSelect(user);

    if (!this.options.multi) {
      this.close();
    }
  }
}
